import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Card, CircularProgress, Stack, Button } from "@mui/joy";
import ProfileForm from "components/admin/ProfileForm";
import UserInfo from "components/admin/UserInfo";
import ProfileCardHeader from "components/admin/ProfileCardHeader";
import { fetchAllApplicantsData } from "../../redux/adminSlice";
import { AppDispatch, RootState } from "../../redux/store";
import BackBtn from "../../assets/svgs/Back-btn.svg";
import { convertUTCToPacificDisplay } from "../../utils/helpers";
import { formatNumber } from "libphonenumber-js";
import { Download } from "@mui/icons-material";
import S3_URL from "utils/s3";
import { getEmployer, getEmployerPosition } from "../../apiconfig/api";

const ApplicantProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { id: applicantId } = useParams<{ id: string }>();
  
  // Get the return URL from location state or construct from referrer
  const returnToApplicantsUrl = (location.state as any)?.returnUrl || 
    (document.referrer && document.referrer.includes('/admin/applicants') ? document.referrer.split(window.location.origin)[1] : '/admin/applicants');
  const [isDownloading, setIsDownloading] = useState(false);
  const [employerName, setEmployerName] = useState<string | undefined>(undefined);
  const [positionName, setPositionName] = useState<string | undefined>(undefined);
  const applicantData = useSelector(
    (state: RootState) => state.admin?.applicantsData?.data?.user
  );
  const loading = useSelector((state: RootState) => state.admin.status);

  const payload = {
    userUuid: applicantId,
    zipCode: "",
    positions: [],
    verifiedPhone: null,
    hasVideo: null,
    hasResume: null,
    superApplied: null,
    minRating: null,
    maxRating: null,
    isPublished: null,
    isActive: null,
    isGloballyVisible: null,
    fullNameLike: null,
    page: 1,
    limit: 10,
    sort: "ASC",
    lastActiveStartUtc: "",
    lastActiveEndUtc: "",
  };

  useEffect(() => {
    dispatch(fetchAllApplicantsData(payload));
  }, [dispatch, applicantId]);

  // Fetch employer and position information
  useEffect(() => {
    const fetchEmployerAndPosition = async () => {
      const applicant = applicantData?.applicants?.[0];
      if (!applicant) return;

      console.log('Applicant data:', applicant);
      console.log('submittedToEmployerId:', applicant.submittedToEmployerId);
      console.log('submittedToEmployerPositionId:', applicant.submittedToEmployerPositionId);

      // Fetch employer name
      if (applicant.submittedToEmployerId) {
        try {
          const employerResponse = await getEmployer(applicant.submittedToEmployerId);
          console.log('Employer response:', employerResponse);
          setEmployerName(employerResponse.data?.employer?.companyName);
        } catch (error) {
          console.error('Error fetching employer:', error);
        }
      }

      // Fetch position name
      if (applicant.submittedToEmployerId && applicant.submittedToEmployerPositionId) {
        try {
          const positionResponse = await getEmployerPosition(
            applicant.submittedToEmployerId,
            applicant.submittedToEmployerPositionId
          );
          console.log('Position response:', positionResponse);
          setPositionName(positionResponse.data?.position?.displayNameOverride);
        } catch (error) {
          console.error('Error fetching position:', error);
        }
      }
    };

    fetchEmployerAndPosition();
  }, [applicantData]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleDownloadResume = async () => {
    const applicant = applicantData?.applicants?.[0];
    if (!applicant?.resumeDocumentId || !applicant?.resume?.originalFileKey) {
      console.error('No resume available for download');
      return;
    }

    try {
      setIsDownloading(true);
      
      // Get file extension from originalFileKey
      const fileExtension = applicant.resume.originalFileKey.split('.').pop() || 'pdf';
      
      // Construct the S3 URL for the resume using the same S3_URL as GeekPlayer
      const s3Url = `${S3_URL}documents/${applicant.resume.originalFileKey}`;
      
      // Create a temporary link to download the file
      const response = await fetch(s3Url);
      if (!response.ok) {
        throw new Error('Failed to download resume');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${applicantData?.fullName} resume.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading resume:', error);
      // You might want to show a toast notification here
    } finally {
      setIsDownloading(false);
    }
  };

  console.log("applicantData", applicantData);
  return (
    <>
      {loading === "loading" ? (
        <div className="flex justify-center items-center h-[100vh]">
          <CircularProgress size="lg" />
        </div>
      ) : (
        <Box className="min-h-screen bg-[#f4f4f4] flex flex-col px-4 py-6 lg:px-10">
          <Box py="5px" className="max-w-2xl mx-auto w-full">
            <Box
              sx={{
                mb: { xs: 1, lg: 2 },
                display: "flex",
                justifyContent: "start",
                alignItems: "start",
              }}
            >
              <Box
                component="img"
                onClick={handleBackClick}
                src={BackBtn}
                alt="Back"
                sx={{
                  width: { xs: 24, lg: 32 },
                  height: { xs: 24, lg: 32 },
                  cursor: "pointer",
                }}
              />
            </Box>
            <Stack
              spacing={2}
              sx={{
                maxWidth: { xs: "100%", md: "650px" },
                mx: "auto",
                mb: 4,
                mt: 2,
              }}
            >
              <Card sx={{ width: "100%", p: 0 }}>
                {applicantData?.applicants?.length > 0 &&
                  applicantData?.applicants[0]?.videoUuid && (
                    <ProfileCardHeader
                      videoSrc={`processed/${applicantData.applicants[0].videoUuid}/${applicantData.applicants[0].videoUuid}-geek-applicant.mp4`}
                    />
                  )}

                <Box p={2}>
                  <UserInfo
                    name={applicantData?.fullName || ""}
                    userId={applicantData?.applicants?.[0]?.userId || ""}
                    phone={
                      formatNumber(
                        applicantData?.phoneNo || "",
                        "US",
                        "INTERNATIONAL"
                      ) || ""
                    }
                    postcode={applicantData?.applicants?.[0]?.zipCode || ""}
                    createdAt={
                      applicantData?.createdAt
                        ? convertUTCToPacificDisplay(applicantData.createdAt) ||
                          ""
                        : ""
                    }
                    superApplied={
                      applicantData?.applicants?.[0]?.superApplied || false
                    }
                    employerName={employerName}
                    positionName={positionName}
                    downloadButton={
                      applicantData?.applicants?.[0]?.resumeDocumentId && 
                      applicantData?.applicants?.[0]?.resume?.originalFileKey ? (
                        <Button
                          size="sm"
                          variant="plain"
                          startDecorator={<Download />}
                          onClick={handleDownloadResume}
                          loading={isDownloading}
                          sx={{
                            color: '#2e2d7e',
                            '&:hover': {
                              backgroundColor: '#F8F9FA'
                            }
                          }}
                        >
                          Download Resume
                        </Button>
                      ) : undefined
                    }
                  />
                </Box>
              </Card>

              <Box px={2} pb={2}>
                <ProfileForm returnToApplicantsUrl={returnToApplicantsUrl} />
              </Box>
            </Stack>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ApplicantProfilePage;
